export const LINE_OAUTH_MESSAGE_TYPE = "line-oauth-complete" as const;
export const LINE_OAUTH_POPUP_STORAGE_KEY = "wizmate.line-oauth-complete";
export const LINE_OAUTH_CONTEXT_STORAGE_KEY = "wizmate.line-oauth-context";

export type LineOAuthPopupResult = "success" | "error";

export type LineOAuthPopupMessage = {
  type: typeof LINE_OAUTH_MESSAGE_TYPE;
  lineConnect: LineOAuthPopupResult;
  errorCode: string | null;
  friendshipStatusChanged: boolean | null;
};

export type LineOAuthPopupSnapshot = LineOAuthPopupMessage & {
  completedAt: number;
};

export type LineOAuthContextSnapshot = {
  returnTo: string;
  returnState?: unknown;
};

function normalizeBoolean(value: string | null): boolean | null {
  if (value === "true") return true;
  if (value === "false") return false;
  return null;
}

export function isLineOAuthPopupMessage(value: unknown): value is LineOAuthPopupMessage {
  if (!value || typeof value !== "object") {
    return false;
  }

  const payload = value as Partial<LineOAuthPopupMessage>;
  return payload.type === LINE_OAUTH_MESSAGE_TYPE;
}

export function createLineOAuthPopupSnapshot(
  message: LineOAuthPopupMessage,
): LineOAuthPopupSnapshot {
  return {
    ...message,
    completedAt: Date.now(),
  };
}

export function isLineOAuthPopupSnapshot(value: unknown): value is LineOAuthPopupSnapshot {
  if (!isLineOAuthPopupMessage(value)) {
    return false;
  }

  const snapshot = value as Partial<LineOAuthPopupSnapshot>;
  return typeof snapshot.completedAt === "number" && Number.isFinite(snapshot.completedAt);
}

export function readLineOAuthPopupMessage(search: string): LineOAuthPopupMessage {
  const params = new URLSearchParams(search);
  const errorCode = params.get("error");
  const hasCode = Boolean(params.get("code"));

  return {
    type: LINE_OAUTH_MESSAGE_TYPE,
    lineConnect: errorCode ? "error" : hasCode ? "success" : "error",
    errorCode,
    friendshipStatusChanged: normalizeBoolean(params.get("friendship_status_changed")),
  };
}

export function writeLineOAuthPopupSnapshot(snapshot: LineOAuthPopupSnapshot) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(LINE_OAUTH_POPUP_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // noop
  }
}

export function readLineOAuthPopupSnapshot() {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(LINE_OAUTH_POPUP_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as unknown;
    return isLineOAuthPopupSnapshot(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function clearLineOAuthPopupSnapshot() {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(LINE_OAUTH_POPUP_STORAGE_KEY);
  } catch {
    // noop
  }
}

export function writeLineOAuthContextSnapshot(snapshot: LineOAuthContextSnapshot) {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(LINE_OAUTH_CONTEXT_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // noop
  }
}

export function readLineOAuthContextSnapshot() {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(LINE_OAUTH_CONTEXT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<LineOAuthContextSnapshot>;
    if (!parsed || typeof parsed.returnTo !== "string") {
      return null;
    }

    return {
      returnTo: parsed.returnTo,
      returnState: parsed.returnState,
    };
  } catch {
    return null;
  }
}

export function clearLineOAuthContextSnapshot() {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.removeItem(LINE_OAUTH_CONTEXT_STORAGE_KEY);
  } catch {
    // noop
  }
}

export const LINE_OAUTH_TOKEN_STORAGE_KEY = "wizmate.line-oauth-token";

export function writeLineOAuthTokenSnapshot(token: string) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(LINE_OAUTH_TOKEN_STORAGE_KEY, token);
  } catch {
    // noop
  }
}

export function readLineOAuthTokenSnapshot(): string | null {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage.getItem(LINE_OAUTH_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearLineOAuthTokenSnapshot() {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(LINE_OAUTH_TOKEN_STORAGE_KEY);
  } catch {
    // noop
  }
}
